import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import type { Filters, SearchRequest, SearchResponse } from "@/types";

export interface HuntForm {
  query: string;
  preset: string;
  filters: Filters;
}

interface HuntStore {
  // Persisted (sessionStorage): the last search so leaving Hunt and coming back —
  // or a page reload within the tab — keeps the current view.
  form: HuntForm;
  result: SearchResponse | null;
  setForm: (f: Partial<HuntForm>) => void;
  setResult: (r: SearchResponse | null) => void;

  // Transient (NOT persisted): a one-shot search to replay, set by History /
  // saved-search favorites, consumed once by the Hunt screen.
  replay: SearchRequest | null;
  setReplay: (r: SearchRequest | null) => void;
}

const EMPTY_FORM: HuntForm = { query: "", preset: "", filters: {} };

// sessionStorage.setItem throws synchronously on quota-exceeded (zustand v5 does
// not catch it) — swallow so a large result set can never crash a state update.
const safeSessionStorage = {
  getItem: (name: string) => sessionStorage.getItem(name),
  setItem: (name: string, value: string) => {
    try {
      sessionStorage.setItem(name, value);
    } catch {
      /* quota exceeded / storage unavailable — skip persisting this update */
    }
  },
  removeItem: (name: string) => sessionStorage.removeItem(name),
};

export const useHuntStore = create<HuntStore>()(
  persist(
    (set) => ({
      form: EMPTY_FORM,
      result: null,
      setForm: (f) => set((s) => ({ form: { ...s.form, ...f } })),
      setResult: (result) => set({ result }),

      replay: null,
      setReplay: (replay) => set({ replay }),
    }),
    {
      name: "repohunt-hunt",
      storage: createJSONStorage(() => safeSessionStorage),
      // Persist the search view (form + results) — never the one-shot replay
      // trigger, and null out rate_limit_remaining: it's a live counter that goes
      // stale on reload and would show a misleading badge after F5 (FE-8). The
      // in-memory store keeps the live value during the session.
      partialize: (s) => ({
        form: s.form,
        result: s.result ? { ...s.result, rate_limit_remaining: null } : null,
      }),
    },
  ),
);
